import React, { useState, useEffect } from 'react';
import { RefreshCw, Zap, Download, CheckCircle2 } from 'lucide-react';
import { fetchSubscriptionMetrics, downloadCSV } from '../utils/api';

export const SubscriptionsPage: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [dataMode, setDataMode] = useState<'test' | 'live'>('live');
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  useEffect(() => {
    loadSubscriptions(false);
    const handleModeChanged = () => {
      loadSubscriptions(false);
    };
    window.addEventListener('system-mode-changed', handleModeChanged);
    return () => window.removeEventListener('system-mode-changed', handleModeChanged);
  }, []);

  const loadSubscriptions = async (notify = true) => {
    setLoading(true);
    const result = await fetchSubscriptionMetrics();
    setData(result);
    if (result?.mode) {
      setDataMode(result.mode);
    }
    setLoading(false);
    if (notify) {
      showToast('✅ Billing & Subscriber records synchronized with SquadCo gateway');
    }
  };

  const handleExportCSV = () => {
    const recent = data?.recent || [];
    const headers = ['Subscription ID', 'User', 'Plan', 'Amount NGN', 'Date', 'Status', 'Payment Ref'];
    const rows = recent.map((s: any) => [
      `"${s.id || ''}"`,
      `"${(s.user || '').replace(/"/g, '""')}"`,
      `"${s.plan || ''}"`,
      s.amountNGN || 500,
      `"${s.date || ''}"`,
      `"${s.status || 'active'}"`,
      `"${s.paymentRef || ''}"`,
    ]);
    const csv = [headers.join(','), ...rows.map((r: string[]) => r.join(','))].join('\n');
    downloadCSV(`amoura-subscriptions-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    showToast(`✅ Exported ${recent.length} subscription records to CSV`);
  };

  const plans = (data?.plans && data.plans.length > 0)
    ? data.plans.map((p: any) => ({
        name: p.name,
        price: `₦${(p.priceNGN || 500).toLocaleString()} ${p.name.includes('Weekly') ? '/ week' : p.name.includes('Hour') ? '/ session' : '/ ticket'}`,
        subscribers: p.subscribers ?? 0,
        mrr: `₦${(p.mrrNGN ?? 0).toLocaleString()}`,
        churn: p.churn || '0%',
        color: p.name.includes('Weekly') ? '#8b5cf6' : p.name.includes('Hour') ? '#f59e0b' : '#3b82f6',
        border: p.name.includes('Weekly') ? 'rgba(139,92,246,0.3)' : p.name.includes('Hour') ? 'rgba(245,158,11,0.3)' : 'rgba(59,130,246,0.3)',
        bg: p.name.includes('Weekly') ? 'rgba(139,92,246,0.1)' : p.name.includes('Hour') ? 'rgba(245,158,11,0.1)' : 'rgba(59,130,246,0.1)',
        features: p.features || [],
        growth: 'Live',
      }))
    : [
        {
          name: 'Elite Weekly Pass',
          price: '₦5,000 / week',
          subscribers: 0,
          mrr: '₦0',
          churn: '0%',
          color: '#8b5cf6',
          border: 'rgba(139,92,246,0.3)',
          bg: 'rgba(139,92,246,0.1)',
          features: [
            'Unlimited chat with all matches',
            'Priority discovery feed rank',
            'Trust score boost (+10 points)',
            'See who liked your profile',
            'Rewind accidental left swipes',
          ],
          growth: 'Live',
        },
        {
          name: '2-Hour Chat Session',
          price: '₦500 / session',
          subscribers: 0,
          mrr: '₦0',
          churn: '0%',
          color: '#f59e0b',
          border: 'rgba(245,158,11,0.3)',
          bg: 'rgba(245,158,11,0.1)',
          features: [
            'Continuous E2EE text chat for 2 hours',
            'Local photo sharing permission',
            'Encrypted WebSocket session',
            'No monthly recurring commitment',
          ],
          growth: 'Live',
        },
        {
          name: 'Instant Match Ticket',
          price: '₦500 / ticket',
          subscribers: 0,
          mrr: '₦0',
          churn: '0%',
          color: '#3b82f6',
          border: 'rgba(59,130,246,0.3)',
          bg: 'rgba(59,130,246,0.1)',
          features: [
            'Instant mutual match bypass',
            'Send introductory direct message',
            'Skip the discovery queue wait',
          ],
          growth: 'Live',
        },
      ];

  const recentTransactions = data?.recent || [];

  return (
    <div className="space-y-6">
      {/* ── Toast Notification ── */}
      {toastMessage && (
        <div className="fixed top-20 right-6 z-50 flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[#141428] border border-emerald-500/40 text-white shadow-2xl shadow-emerald-500/10">
          <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
          <p className="text-xs font-bold">{toastMessage}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white font-['Outfit']">
              Subscription Plans & Revenue (SquadCo)
            </h1>
            {dataMode === 'test' ? (
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1.5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                🧪 Test Sandbox MRR
              </span>
            ) : (
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1.5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                ⚡ Live SquadCo / D1
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-400 mt-1">
            {dataMode === 'test'
              ? 'Simulated subscriber metrics and test payment logs for QA sandbox'
              : 'Naira pricing tiers, active subscribers, and automated webhook provisioning'}
          </p>
        </div>
        <div className="flex items-center gap-2.5 self-start sm:self-auto">
          <button
            onClick={() => loadSubscriptions(true)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-[#141428] border border-[#1e1e3a] text-zinc-300 hover:text-white transition-colors cursor-pointer"
            title="Synchronize billing with SquadCo gateway"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Sync Billing
          </button>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold bg-[#141428] border border-[#1e1e3a] text-zinc-300 hover:text-white transition-colors cursor-pointer"
            title="Export recent subscriptions as CSV"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Top Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-5 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-zinc-400">Total Revenue Volume</span>
            <span className="text-xs font-bold text-emerald-400">Live D1</span>
          </div>
          <p className="text-2xl font-extrabold text-white font-['Outfit']">
            ₦{(data?.totalRevenueNGN ?? 0).toLocaleString()}
          </p>
          <p className="text-xs text-zinc-400 mt-1">SquadCo gross volume processed</p>
        </div>

        <div className="p-5 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-zinc-400">Paying Subscribers</span>
            <span className="text-xs font-bold text-emerald-400">Live D1</span>
          </div>
          <p className="text-2xl font-extrabold text-white font-['Outfit']">
            {(data?.totalSubscribers ?? 0).toLocaleString()}
          </p>
          <p className="text-xs text-zinc-400 mt-1">Active users with unlocked features</p>
        </div>

        <div className="p-5 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-zinc-400">Average Ticket Size</span>
            <span className="text-xs font-bold text-purple-400">Live ARPU</span>
          </div>
          <p className="text-2xl font-extrabold text-white font-['Outfit']">
            ₦{data?.totalSubscribers ? Math.round((data.totalRevenueNGN || 0) / data.totalSubscribers).toLocaleString() : '0'}
          </p>
          <p className="text-xs text-zinc-400 mt-1">Average revenue per subscriber</p>
        </div>
      </div>

      {/* Plan Details Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan: any, i: number) => (
          <div
            key={i}
            className="flex flex-col justify-between rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] p-6 shadow-xl hover:border-zinc-500 transition-all"
          >
            <div>
              <div className="flex items-center justify-between mb-4">
                <span
                  className="px-3 py-1 text-xs font-bold rounded-lg"
                  style={{ backgroundColor: plan.bg, color: plan.color }}
                >
                  {plan.name}
                </span>
                <span className="text-xs font-semibold text-emerald-400">{plan.growth}</span>
              </div>

              <p className="text-3xl font-extrabold text-white font-['Outfit'] mb-1">
                {plan.price || `₦${plan.priceNGN?.toLocaleString()}`}
              </p>
              <p className="text-xs text-zinc-400 mb-6">Provisioned automatically via Squad webhook</p>

              <div className="space-y-3 py-4 border-t border-b border-[#1e1e3a] mb-6">
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-400">Active Subscribers</span>
                  <span className="font-bold text-white">{plan.subscribers.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-400">Revenue Contribution</span>
                  <span className="font-bold text-white">
                    {plan.mrr || `₦${plan.mrrNGN?.toLocaleString()}`}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-400">Churn Rate</span>
                  <span className="font-bold text-amber-400">{plan.churn}</span>
                </div>
              </div>

              <div className="space-y-2 mb-6">
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">Features</p>
                {plan.features.map((feat: string, j: number) => (
                  <div key={j} className="flex items-center gap-2 text-xs text-zinc-300">
                    <Zap size={13} style={{ color: plan.color }} className="shrink-0" />
                    <span>{feat}</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              className="w-full py-2.5 rounded-xl text-xs font-bold text-white transition-all shadow-md"
              style={{ backgroundColor: plan.color }}
            >
              Configure Tier
            </button>
          </div>
        ))}
      </div>

      {/* Recent Subscriptions Table */}
      <div className="rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#1e1e3a]">
          <h2 className="text-base font-bold text-white">Recent Subscription Provisions</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#141428] text-xs font-bold uppercase tracking-wider text-zinc-400 border-b border-[#1e1e3a]">
              <tr>
                <th className="px-6 py-3">User</th>
                <th className="px-6 py-3">Plan</th>
                <th className="px-6 py-3">Amount</th>
                <th className="px-6 py-3">Payment Ref</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1e1e3a]">
              {recentTransactions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-xs text-zinc-500">
                    No active subscriptions provisioned yet in Cloudflare D1. Subscriptions will display here automatically as customers pay via Squad.
                  </td>
                </tr>
              ) : (
                recentTransactions.map((tx: any, i: number) => {
                  const planName = tx.plan === 'chat_week' ? 'Elite Weekly' : tx.plan === 'chat_2hr' ? '2-Hour Chat' : tx.plan === 'match' ? 'Instant Match' : (tx.plan || 'Elite Weekly');
                  const amount = tx.amount_ngn || tx.amountNGN || 500;
                  const ref = tx.payment_ref || tx.paymentRef || tx.paystack_ref || tx.id || '—';
                  const date = tx.date || (tx.created_at ? tx.created_at.slice(0, 10) : 'Recently');
                  return (
                    <tr key={i} className="hover:bg-[#141428]/50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-white">{tx.user}</td>
                      <td className="px-6 py-4 text-xs font-semibold text-purple-400">{planName}</td>
                      <td className="px-6 py-4 font-bold text-emerald-400">
                        ₦{amount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-xs font-mono text-zinc-400">{ref}</td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-0.5 text-xs font-bold rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 capitalize">
                          {tx.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-zinc-400">{date}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
